import { Mail, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import logo from "@/assets/equalaccess-logo.png";

const Footer = () => {
  const { t } = useLanguage();
  
  return (
    <footer className="bg-card border-t border-border py-12" role="contentinfo">
      <div className="container mx-auto px-4">
        {/* Top section: Logo, Name & Slogan - Centered */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-3">
            <img src={logo} alt="" className="h-12 w-12 object-contain" aria-hidden="true" />
            <p className="text-2xl font-bold text-foreground">EqualAccess B.V.</p>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('footer.tagline')}
          </p>
        </div>
        
        {/* Main content grid */}
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Quick Links */}
          <nav aria-labelledby="footer-links-heading">
            <h3 id="footer-links-heading" className="font-semibold mb-4 text-foreground">{t('footer.links.title')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/services" className="text-muted-foreground hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1 inline-block min-h-[24px]">
                  {t('footer.links.services')}
                </Link>
              </li>
              <li>
                <Link to="/approach" className="text-muted-foreground hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1 inline-block min-h-[24px]">
                  {t('footer.links.approach')}
                </Link>
              </li>
              <li>
                <Link to="/kennisbank/digitale-toegankelijkheid" className="text-muted-foreground hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1 inline-block min-h-[24px]">
                  {t('nav.knowledge')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-muted-foreground hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1 inline-block min-h-[24px]">
                  {t('footer.links.about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-muted-foreground hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1 inline-block min-h-[24px]">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </nav>
          
          {/* Contact Information */}
          <address className="not-italic">
            <h3 className="font-semibold mb-4 text-foreground">{t('footer.contact.title')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-muted-foreground">
                <Mail className="w-4 h-4 text-accent mt-1 flex-shrink-0" aria-hidden="true" />
                <a href="mailto:info@equalaccess.nl" className="hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-1">
                  info@equalaccess.nl
                </a>
              </li>
              <li className="flex items-start gap-2 text-muted-foreground">
                <Phone className="w-4 h-4 text-accent mt-1 flex-shrink-0" aria-hidden="true" />
                <a href="tel:+31610085363" className="hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-1">
                  +31 6 1008 5363
                </a>
              </li>
            </ul>
          </address>

          {/* Company Information */}
          <div>
            <h3 className="font-semibold mb-4 text-foreground">{t('footer.company.title')}</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>{t('footer.company.name')}</li>
              <li>{t('footer.company.kvk')}</li>
              <li>{t('footer.company.btw')}</li>
            </ul>
          </div>

          {/* Legal Links */}
          <nav aria-label={t('footer.legal.title')}>
            <h3 className="font-semibold mb-4 text-foreground">{t('footer.legal.title')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/privacy" className="text-muted-foreground hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1 inline-block min-h-[24px]">
                  {t('footer.legal.privacy')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-muted-foreground hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1 inline-block min-h-[24px]">
                  {t('footer.legal.terms')}
                </Link>
              </li>
              <li>
                <Link to="/accessibility" className="text-muted-foreground hover:text-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-2 py-1 inline-block min-h-[24px]">
                  {t('footer.legal.accessibility')}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
        
        {/* Copyright */}
        <div className="pt-8 border-t border-border text-center text-muted-foreground text-sm">
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
